import { useState } from 'react';
import {
  useAddNewCrmStatusMutation,
  useDeleteCrmStatusMutation,
  useEditCrmStatusMutation,
  useUpdateCrmItemMutation,
} from '../../../../reduxToolkit/reducers/usersSlice';
import { useSwalNotification } from '../../../../hooks/useSwalNotification';
import { useTranslation } from 'react-i18next';

export const useCrmControlActions = () => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [updateCrmItem] = useUpdateCrmItemMutation();
  const [addNewCrmStatus] = useAddNewCrmStatusMutation();
  const [editCrmStatus] = useEditCrmStatusMutation();
  const [deleteCrmStatus] = useDeleteCrmStatusMutation();
  const { showSwalNotification } = useSwalNotification();
  const submitComment = async (payload: {
    crmControlId: number | null;
    comment: string;
    selectedAction: { id: number; statusName: string } | null;
    newFiles: File[];
    crmItemSource: string;
    onSuccess?: () => void;
    onError?: (error: any) => void;
  }) => {
    if (!payload.crmControlId) return;
    const fd = new FormData();
    fd.append('crmControlId', String(payload.crmControlId));
    fd.append('comment', payload.comment);
    fd.append('actionId', payload.selectedAction ? String(payload.selectedAction.id) : '');
    fd.append('actionName', payload.selectedAction ? payload.selectedAction.statusName : '');
    fd.append('source', payload.crmItemSource);
    if (payload.newFiles.length) {
      payload.newFiles.forEach((file) => fd.append('screenshots', file));
    }
    try {
      setSubmitting(true);
      await updateCrmItem(fd).unwrap();
      setSubmitting(false);
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.successTitle') || 'Success',
        text: t('leads.notifications.successText') || 'Comment submitted successfully',
        icon: 'success',
      });
    } catch (error) {
      setSubmitting(false);
      console.log('error', error);
      showSwalNotification({
        title: t('leads.notifications.errorCommentTitle') || 'Error',
        text: t('leads.notifications.errorUpdateText') || 'Could not submit comment',
        icon: 'error',
      });
    }
  };

  const saveEditStatus = async (payload: {
    selectedAction: { id: number; statusName: string } | null;
    editAction: string;
    colorInputValue: string;
    onSuccessCb?: () => void;
    onErrorCb?: () => void;
  }) => {
    try {
      await editCrmStatus({
        actionId: payload.selectedAction ? Number(payload.selectedAction.id) : 0,
        statusName: payload.editAction,
        colorCode: payload.colorInputValue,
      }).unwrap();
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('global.statusMutationsText.successEditTitle'),
        text: t('global.statusMutationsText.successEditText'),
        icon: 'success',
        confirmButtonText: t('global.ok'),
        confirmCallback: () => {
          new Promise((r) => setTimeout(r, 300)).then(() => {
            payload.onSuccessCb && payload.onSuccessCb();
          });
        },
      });
    } catch (err) {
      console.error('failed to edit action', err);
      showSwalNotification({
        title: t('global.statusMutationsText.errorTitleEdit') || 'Error',
        text: t('global.statusMutationsText.errorTextEdit') || 'Could not edit action',
        icon: 'error',
        confirmButtonText: t('global.ok'),
        confirmCallback: () => {
          new Promise((r) => setTimeout(r, 300)).then(() => {
            payload.onErrorCb && payload.onErrorCb();
          });
        },
      });
    }
  };
  const addNewStatus = async (payload: { newAction: string; colorInputValue: string }) => {
    try {
      const res = await addNewCrmStatus({
        statusName: payload.newAction,
        colorCode: payload.colorInputValue,
      }).unwrap();
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('global.statusMutationsText.successAddTitle') || 'Success',
        text: t('global.statusMutationsText.successAddText') || 'New status added successfully',
        icon: 'success',
      });
      return res;
    } catch (err) {
      console.error('failed to add new action', err);
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('leads.notifications.errorTitle') || 'Error',
        text: t('leads.notifications.errorText') || 'Could not add action',
        icon: 'error',
      });
    }
  };

  const deleteStatusConfirmed = async (payload: {
    actionId: number;
    onSuccess?: () => void;
    onError?: (error: any) => void;
  }) => {
    try {
      await deleteCrmStatus({ actionId: payload.actionId }).unwrap();
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('global.statusMutationsText.successDeleteTitle'),
        text: t('global.statusMutationsText.successDeleteText'),
        icon: 'success',
      });
    } catch (err) {
      console.error('failed to delete action', err);
      await new Promise((r) => setTimeout(r, 500));
      showSwalNotification({
        title: t('global.statusMutationsText.errorTitleDelete'),
        text: t('global.statusMutationsText.errorTextDelete'),
        icon: 'error',
      });
    }
  };
  return {
    submitComment,
    saveEditStatus,
    addNewStatus,
    deleteStatusConfirmed,
    submitting,
  };
};
